import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Skeleton, Button, Chip, Grid, Divider, Avatar, Card, CardContent, CardHeader } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import { api } from '../../services/api';

// Types
interface UserProfile {
  user_id: number;
  username: string;
  email_hash?: string;
  phone_hash?: string;
  status: string;
  kyc_status: string;
  created_at: string;
  updated_at?: string;
}

// Helper functions for rendering status chips
const renderKycStatus = (status: string) => {
  switch (status) {
    case 'verified':
      return <Chip icon={<VerifiedUserIcon />} label="Верифицирован" color="success" />;
    case 'pending':
      return <Chip icon={<PendingIcon />} label="В ожидании" color="warning" />;
    case 'rejected':
      return <Chip icon={<CancelIcon />} label="Отклонен" color="error" />;
    case 'not_verified':
      return <Chip icon={<PersonIcon />} label="Не верифицирован" color="default" />;
    default:
      return <Chip label={status || 'Не определен'} color="default" />;
  }
};

const renderUserStatus = (status: string) => {
  switch (status) {
    case 'active':
      return <Chip label="Активен" color="success" />;
    case 'blocked':
      return <Chip label="Заблокирован" color="error" />;
    case 'suspended':
      return <Chip label="Приостановлен" color="warning" />;
    default:
      return <Chip label={status || 'Не определен'} color="default" />;
  }
};

const KycUserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        const response = await api.get(`/users/${userId}`);
        setUserData(response.data.data);
        setError(null);
      } catch (err) {
        setError('Failed to fetch user data');
        setUserData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  const handleBackToList = () => {
    navigate('/kyc_users');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  if (error || !userData) {
    return (
      <Box>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToList}
          sx={{ mb: 2 }}
        >
          Назад к списку
        </Button>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" color="error">
            Ошибка при загрузке профиля
          </Typography>
          <Typography variant="body1">
            Пользователь не найден или произошла ошибка при загрузке. Пожалуйста, попробуйте позже.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const user = userData;

  return (
    <Box>
      {/* Header and back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToList}
          sx={{ mr: 2 }}
        >
          Назад
        </Button>
        <Typography variant="h4">
          Профиль пользователя #{user.user_id}
        </Typography>
      </Box>
      
      <Grid container spacing={3}>
        {/* User main info */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader 
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {user.username.charAt(0).toUpperCase()}
                </Avatar>
              }
              title={
                <Typography variant="h6">
                  {user.username}
                </Typography>
              }
              subheader={`ID: ${user.user_id}`}
            />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Статус аккаунта
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {renderUserStatus(user.status)}
                </Box>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Статус KYC
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {renderKycStatus(user.kyc_status)}
                </Box>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Дата регистрации
                </Typography>
                <Typography variant="body2">
                  {new Date(user.created_at).toLocaleString()}
                </Typography>
              </Box>
              
              {user.updated_at && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Последнее обновление
                  </Typography>
                  <Typography variant="body2">
                    {new Date(user.updated_at).toLocaleString()}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ mt: 3 }}>
                <Button 
                  variant="contained" 
                  startIcon={<DocumentScannerIcon />}
                  onClick={() => navigate(`/kyc/documents?user_id=${userId}`)}
                  fullWidth
                >
                  Просмотр документов KYC
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Personal info */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Личная информация
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {user.email_hash || 'Не указано'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Телефон
                </Typography>
                <Typography variant="body1">
                  {user.phone_hash || 'Не указано'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default KycUserProfilePage; 